import React, { useEffect, useState } from "react";
import AdminLayout from "./AdminLayout.js";

const RANGES = [
  { key: "daily",   label: "Today" },
  { key: "weekly",  label: "This Week" },
  { key: "monthly", label: "This Month" },
  { key: "yearly",  label: "This Year" },
  { key: "all",     label: "All Time" }
];

const EVENT_CONFIG = {
  site_visit:       { label: "Site Visit",       color: "#6366f1", bg: "#eef2ff", icon: "◎" },
  product_view:     { label: "Product Viewed",   color: "#0ea5e9", bg: "#e0f2fe", icon: "◈" },
  add_to_cart:      { label: "Added to Cart",    color: "#f59e0b", bg: "#fef3c7", icon: "◫" },
  checkout_started: { label: "Checkout Started", color: "#f97316", bg: "#fff7ed", icon: "◷" },
  order_placed:     { label: "Order Placed",     color: "#10b981", bg: "#d1fae5", icon: "✓" }
};

function timeAgo(date) {
  const diff = Math.floor((Date.now() - new Date(date)) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return new Date(date).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export default function AdminAnalyticsScreen() {
  const [range, setRange] = useState("weekly");
  const [summary, setSummary] = useState(null);
  const [funnel, setFunnel] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAll();
  }, [range]);

 const fetchAll = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
      const [summaryRes, funnelRes, topRes, eventsRes] = await Promise.all([
        fetch(`https://eleven11-i6v4.onrender.com/api/analytics/summary?range=${range}`, { headers: authHeaders }),
        fetch(`https://eleven11-i6v4.onrender.com/api/analytics/funnel?range=${range}`, { headers: authHeaders }),
        fetch(`https://eleven11-i6v4.onrender.com/api/analytics/top-products?range=${range}`, { headers: authHeaders }),
        fetch(`https://eleven11-i6v4.onrender.com/api/analytics/events?range=${range}&limit=30`, { headers: authHeaders })
      ]);

      const summaryData = await summaryRes.json();
      const funnelData = await funnelRes.json();
      const topData = await topRes.json();
      const eventsData = await eventsRes.json();

      setSummary(summaryData);
      setFunnel(funnelData.funnel || []);
      setTopProducts(topData.topProducts || []);
      setEvents(eventsData.events || []);
    } catch (err) {
      console.error("Analytics fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const METRIC_CARDS = summary ? [
    { label: "Site Visits",        value: summary.totalVisits,       sub: `${summary.todayVisits} today`,   color: "#6366f1" },
    { label: "Product Views",      value: summary.totalProductViews, sub: "Unique product pages",            color: "#0ea5e9" },
    { label: "Cart Adds",          value: summary.totalAddToCart,    sub: "Items added to cart",             color: "#f59e0b" },
    { label: "Checkouts Started",  value: summary.totalCheckouts,    sub: "Initiated checkout",              color: "#f97316" },
    { label: "Orders Placed",      value: summary.totalOrders,       sub: `${summary.todayOrders} today`,   color: "#10b981" },
    { label: "Revenue",            value: `$${Number(summary.revenue || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`, sub: "Total sales",  color: "#8b5cf6" },
    { label: "Conversion Rate",    value: `${summary.conversionRate}%`, sub: "Visits to orders",            color: "#ec4899" }
  ] : [];

  const maxFunnel = funnel[0]?.count || 1;

  return (
    <AdminLayout>

      {/* Header */}
      <div style={styles.header}>
        <div>
          <p style={styles.kicker}>ELEVEN CONTROL CENTER</p>
          <h1 style={styles.title}>Analytics</h1>
        </div>

        {/* Range Selector */}
        <div style={styles.rangeSelector}>
          {RANGES.map(r => (
            <button
              key={r.key}
              style={{
                ...styles.rangeBtn,
                ...(range === r.key ? styles.rangeBtnActive : {})
              }}
              onClick={() => setRange(r.key)}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {loading && <div style={styles.loadingWrap}>Loading analytics...</div>}

      {!loading && (
        <>
          {/* Metric Cards */}
          <div style={styles.metricsGrid}>
            {METRIC_CARDS.map(card => (
              <div key={card.label} style={styles.metricCard}>
                <div style={{ ...styles.metricAccent, background: card.color }} />
                <p style={styles.metricLabel}>{card.label}</p>
                <h2 style={{ ...styles.metricValue, color: card.color }}>{card.value}</h2>
                <p style={styles.metricSub}>{card.sub}</p>
              </div>
            ))}
          </div>

          {/* Two Column */}
          <div style={styles.twoCol}>

            {/* Conversion Funnel */}
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <h3 style={styles.cardTitle}>Conversion Funnel</h3>
                <span style={styles.cardSub}>
                  {RANGES.find(r => r.key === range)?.label}
                </span>
              </div>

              {funnel.map((step, i) => {
                const pct = maxFunnel > 0 ? Math.round((step.count / maxFunnel) * 100) : 0;
                const dropoff = i > 0 && funnel[i - 1].count > 0
                  ? Math.round((1 - step.count / funnel[i - 1].count) * 100)
                  : null;

                return (
                  <div key={step.stage} style={styles.funnelRow}>
                    <div style={styles.funnelLeft}>
                      <div style={styles.funnelStage}>{step.stage}</div>
                  
                    </div>
                    <div style={styles.funnelBarWrap}>
                      <div
                        style={{
                          ...styles.funnelBar,
                          width: `${pct}%`,
                          background: step.color
                        }}
                      />
                    </div>
                    <div style={{ ...styles.funnelCount, color: step.color }}>
                      {step.count.toLocaleString()}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Top Products */}
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <h3 style={styles.cardTitle}>Top Products</h3>
                <span style={styles.cardSub}>By views</span>
              </div>

              {topProducts.length === 0 && (
                <p style={styles.emptyText}>No product views yet.</p>
              )}

              {topProducts.map((p, i) => (
                <div key={p._id} style={styles.topProductRow}>
                  <div style={styles.topProductRank}>#{i + 1}</div>
                  <div style={styles.topProductInfo}>
                    <p style={styles.topProductTitle}>{p.title || p._id}</p>
                    <p style={styles.topProductViews}>{p.views} view{p.views !== 1 ? "s" : ""}</p>
                  </div>
                  <div style={styles.topProductBar}>
                    <div style={{
                      ...styles.topProductBarFill,
                      width: `${Math.min(100, (p.views / (topProducts[0]?.views || 1)) * 100)}%`
                    }} />
                  </div>
                </div>
              ))}
            </div>

          </div>

          {/* Activity Feed */}
          <div style={{ ...styles.card, marginTop: 20 }}>
            <div style={styles.cardHeader}>
              <h3 style={styles.cardTitle}>Activity Feed</h3>
              <span style={styles.cardSub}>{events.length} events</span>
            </div>

            <div style={styles.feedGrid}>
              {events.length === 0 && (
                <p style={styles.emptyText}>No activity in this period.</p>
              )}
              {events.map((event, i) => {
                const config = EVENT_CONFIG[event.eventType] || { label: event.eventType, color: "#888", bg: "#f5f5f5", icon: "•" };
                return (
                  <div key={event._id || i} style={styles.feedItem}>
                    <div style={{ ...styles.feedBadge, background: config.bg, color: config.color }}>
                      <span>{config.icon}</span>
                      {config.label}
                    </div>
                    <div style={styles.feedMeta}>
                      {event.listingTitle && <span style={styles.feedTitle}>{event.listingTitle}</span>}
                      {event.metadata?.orderNumber && <span style={styles.feedTitle}>{event.metadata.orderNumber}</span>}
                      {event.metadata?.total && (
                        <span style={styles.feedAmount}>${Number(event.metadata.total).toFixed(2)}</span>
                      )}
                    </div>
                    <span style={styles.feedTime}>{timeAgo(event.timestamp)}</span>
                  </div>
                );
              })}
            </div>
          </div>

        </>
      )}

    </AdminLayout>
  );
}

const styles = {

  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: 28
  },

  kicker: {
    margin: 0,
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 2,
    color: "#9ca3af",
    textTransform: "uppercase"
  },

  title: {
    margin: "6px 0 0",
    fontSize: 36,
    fontWeight: 800,
    letterSpacing: -1,
    color: "#0f0f17"
  },

  rangeSelector: {
    display: "flex",
    gap: 6,
    background: "#fff",
    padding: 6,
    borderRadius: 14,
    boxShadow: "0 1px 3px rgba(0,0,0,0.06)"
  },

  rangeBtn: {
    padding: "8px 16px",
    border: "none",
    borderRadius: 10,
    background: "transparent",
    fontSize: 13,
    fontWeight: 600,
    color: "#9ca3af",
    cursor: "pointer",
    transition: "all 0.15s ease"
  },

  rangeBtnActive: {
    background: "#0f0f17",
    color: "#fff"
  },

  loadingWrap: {
    textAlign: "center",
    marginTop: 80,
    fontSize: 16,
    color: "#9ca3af"
  },

  metricsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    gap: 14,
    marginBottom: 20
  },

  metricCard: {
    background: "#ffffff",
    borderRadius: 16,
    padding: "18px 16px",
    position: "relative",
    overflow: "hidden",
    boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 8px 24px rgba(0,0,0,0.04)"
  },

  metricAccent: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 3,
    borderRadius: "16px 16px 0 0"
  },

  metricLabel: {
    margin: "8px 0 8px",
    fontSize: 10,
    fontWeight: 700,
    color: "#9ca3af",
    textTransform: "uppercase",
    letterSpacing: 0.8
  },

  metricValue: {
    margin: 0,
    fontSize: 24,
    fontWeight: 900,
    letterSpacing: -0.5
  },

  metricSub: {
    margin: "5px 0 0",
    fontSize: 11,
    color: "#9ca3af"
  },

  twoCol: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 20
  },

  card: {
    background: "#fff",
    borderRadius: 18,
    padding: "24px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 8px 24px rgba(0,0,0,0.04)"
  },

  cardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 20
  },

  cardTitle: {
    margin: 0,
    fontSize: 15,
    fontWeight: 800,
    color: "#0f0f17"
  },

  cardSub: {
    fontSize: 12,
    color: "#9ca3af",
    fontWeight: 500
  },

  emptyText: {
    color: "#9ca3af",
    fontSize: 14,
    textAlign: "center",
    padding: "20px 0"
  },

  /* Funnel */
  funnelRow: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    marginBottom: 16
  },

  funnelLeft: {
    width: 130,
    flexShrink: 0
  },

  funnelStage: {
    fontSize: 13,
    fontWeight: 600,
    color: "#374151"
  },

  funnelDropoff: {
    fontSize: 11,
    color: "#ef4444",
    marginTop: 2
  },

  funnelBarWrap: {
    flex: 1,
    height: 10,
    background: "#f3f4f6",
    borderRadius: 999,
    overflow: "hidden"
  },

  funnelBar: {
    height: "100%",
    borderRadius: 999,
    transition: "width 0.4s ease"
  },

  funnelCount: {
    fontSize: 14,
    fontWeight: 800,
    width: 50,
    textAlign: "right",
    flexShrink: 0
  },

  /* Top Products */
  topProductRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "10px 0",
    borderBottom: "1px solid #f3f4f6"
  },

  topProductRank: {
    fontSize: 13,
    fontWeight: 800,
    color: "#d1d5db",
    width: 24,
    flexShrink: 0
  },

  topProductInfo: {
    flex: 1,
    overflow: "hidden"
  },

  topProductTitle: {
    margin: 0,
    fontSize: 13,
    fontWeight: 600,
    color: "#374151",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
  },

  topProductViews: {
    margin: "2px 0 0",
    fontSize: 12,
    color: "#9ca3af"
  },

  topProductBar: {
    width: 80,
    height: 4,
    borderRadius: 999,
    background: "#f3f4f6",
    overflow: "hidden",
    flexShrink: 0
  },

  topProductBarFill: {
    height: "100%",
    borderRadius: 999,
    background: "#6366f1",
    transition: "width 0.3s ease"
  },

  /* Activity Feed */
  feedGrid: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    maxHeight: 400,
    overflowY: "auto"
  },

  feedItem: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "8px 0",
    borderBottom: "1px solid #f9fafb"
  },

  feedBadge: {
    display: "flex",
    alignItems: "center",
    gap: 5,
    padding: "4px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 700,
    whiteSpace: "nowrap",
    flexShrink: 0
  },

  feedMeta: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    gap: 8,
    overflow: "hidden"
  },

  feedTitle: {
    fontSize: 13,
    color: "#374151",
    fontWeight: 500,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
  },

  feedAmount: {
    fontSize: 13,
    fontWeight: 700,
    color: "#10b981",
    flexShrink: 0
  },

  feedTime: {
    fontSize: 12,
    color: "#9ca3af",
    flexShrink: 0
  }
};